/**
 * Minimal radix-2 iterative FFT (Cooley–Tukey) and magnitude spectrum.
 * Pure and framework-free so it is unit-testable; used by the Mode-2 drone
 * separation (ADR-0003).
 */
function isPow2(n) {
    return n > 0 && (n & (n - 1)) === 0;
}
/** In-place complex FFT. `re`/`im` must have the same power-of-two length. */
export function fft(re, im) {
    const n = re.length;
    if (!isPow2(n) || im.length !== n) {
        throw new Error('fft: length must be a power of two and re/im must match');
    }
    // Bit-reversal permutation.
    for (let i = 1, j = 0; i < n; i++) {
        let bit = n >> 1;
        for (; j & bit; bit >>= 1)
            j ^= bit;
        j ^= bit;
        if (i < j) {
            const tr = re[i];
            re[i] = re[j];
            re[j] = tr;
            const ti = im[i];
            im[i] = im[j];
            im[j] = ti;
        }
    }
    for (let len = 2; len <= n; len <<= 1) {
        const ang = (-2 * Math.PI) / len;
        const wr = Math.cos(ang);
        const wi = Math.sin(ang);
        for (let i = 0; i < n; i += len) {
            let cwr = 1;
            let cwi = 0;
            const half = len >> 1;
            for (let k = 0; k < half; k++) {
                const a = i + k;
                const b = a + half;
                const vr = re[b] * cwr - im[b] * cwi;
                const vi = re[b] * cwi + im[b] * cwr;
                const ur = re[a];
                const ui = im[a];
                re[a] = ur + vr;
                im[a] = ui + vi;
                re[b] = ur - vr;
                im[b] = ui - vi;
                const nwr = cwr * wr - cwi * wi;
                cwi = cwr * wi + cwi * wr;
                cwr = nwr;
            }
        }
    }
}
/**
 * One-sided magnitude spectrum (bins 0..N/2) of a real frame. A Hann window is
 * applied by default to reduce spectral leakage.
 */
export function magnitudeSpectrum(frame, window = 'hann') {
    const n = frame.length;
    if (!isPow2(n))
        throw new Error('magnitudeSpectrum: frame length must be a power of two');
    const re = new Float64Array(n);
    const im = new Float64Array(n);
    for (let i = 0; i < n; i++) {
        const w = window === 'hann' ? 0.5 * (1 - Math.cos((2 * Math.PI * i) / (n - 1))) : 1;
        re[i] = frame[i] * w;
    }
    fft(re, im);
    const half = n >> 1;
    const mag = new Float32Array(half + 1);
    for (let k = 0; k <= half; k++) {
        mag[k] = Math.hypot(re[k], im[k]);
    }
    return mag;
}
//# sourceMappingURL=fft.js.map